/* eslint-disable no-underscore-dangle */
/**
 * Entry point of the API. Exports the api constructor which holds the requests methods.
 *
 * @author Hugo Azevedo
 */
import createAxiosIntance from './requester';
import { errorStatusInterceptors } from './utils/authUtils';
import authentication from './authentication';

/**
 * This is the API entry point. Use this constructor to build instances of the StRequets
 * @namespace StRequests
 * @example
 * const url = StRequests.utils.getBackendUrl({
 *  serviceName: 'sac-service'
 *  servicePath: 'reply'
 * });
 * const api = new StRequests(url)
 *
 * api.get() // makes a get request
 * api.post() // makes a post request
 * api.put() // makes a put request
 * api.delete() // makes a delete request
 * api.request() // makes a request with the options provided as arguments. (avoid this)
 * @param {string} url used as a base url in this instance
 * @param {axios} _http is the http requester used in this instance
 */
export default function StRequets(url, _http) {
  this.http = null;
  this.url = url;
  this._http = _http;
  this.authOptions = authentication();
  this.defaultOptions = {
    baseURL: '',
    headers: null,
    authOptions: this.authOptions,
    interceptors: {},
    errorStatusInterceptors,
    retry: true,
    maxRetries: 3,
  };
  this.createHttp();
}

StRequets.prototype.createHttp = function createHttp() {
  this.http = this._http || createAxiosIntance(this.defaultOptions, true);
  return this;
};

/**
 * Executes the http request
 * @private
 * @param {string} method holds the http methods ['get', 'post', 'put', 'delete', 'patch']
 * @param {Object} data holds the request data or options depending on the request method
 * @param {Object} options holds the request options
 * @returns {Promise} resolves or rejects as a Promise.
 */
StRequets.prototype.execute = function execute(method, data = {}, options = {}) {
  return this.http[method](`${this.url || ''}${data.url || ''}`, data, options);
};

/**
 * @memberof StRequests
 * @function get
 * @description Executes a GET request
 * @param {Object} options request options
 * @returns {Promise} resolves or rejects as a Promise.
 */
StRequets.prototype.get = function get(...args) {
  // return this.execute('get', ...args);
  return this.execute('get', ...args);
};

/**
* @memberof StRequests
* @function post
* @description Executes a POST request
* @param {Object} options request options
* @returns {Promise} resolves or rejects as a Promise.
*/
StRequets.prototype.post = function post(...args) {
  return this.execute('post', ...args);
};

/**
* @memberof StRequests
* @function put
* @description Executes a PUT request
* @param {Object} options request options
* @returns {Promise} resolves or rejects as a Promise.
*/
StRequets.prototype.put = function put(...args) {
  return this.execute('put', ...args);
};

/**
 * @memberof StRequests
 * @function delete
 * @description Executes a DELETE request
 * @param {Object} options request options
 * @returns {Promise} resolves or rejects as a Promise.
 */
StRequets.prototype.delete = function _delete(...args) {
  return this.execute('delete', ...args);
};

/**
 * @memberof StRequests
 * @function patch
 * @description Executes a PATCH request
 * @param {Object} options request options
 * @returns {Promise} resolves or rejects as a Promise.
 */
StRequets.prototype.patch = function patch(...args) {
  return this.execute('patch', ...args);
};

/**
 * @memberof StRequests
 * @function request
 * @description Executes a request with the given method and options
 * @param {Object} options request options
 * @param {string} options.method http method for the request
 * @returns {Promise} resolves or rejects as a Promise.
 */
StRequets.prototype.request = function request(options) {
  return this.execute(options.method, options);
};

/**
 * @memberof StRequests
 * @function use
 * @description Sets the default properties of this instance
 * @param {Object} options request options
 */
StRequets.prototype.use = function use(options) {
  Object.assign(this.defaultOptions, options);
  this.createHttp();
  return this;
};

/**
 * @memberof StRequests
 * @function getDefaults
 * @description returns an object containing the default properties of this instance
 * @param {Object} options request options
 */
StRequets.prototype.getDefaults = function getDefaults() {
  return Object.assign({}, this.defaultOptions);
};
